import { FACEBOOK_LIMITS, INSTAGRAM_LIMITS } from '../../shared/meta-media-limits.ts';

export const PLATFORM_IDS = [
  'instagram',
  'tiktok',
  'linkedin',
  'twitter',
  'facebook',
  'youtube',
  'pinterest',
  'threads',
] as const;
export type PlatformId = (typeof PLATFORM_IDS)[number];

export interface PlatformSpec {
  image: { aspectRatios: string[]; recommendedAspectRatio: string; maxFileMB: number };
  video: {
    aspectRatios: string[];
    recommendedAspectRatio: string;
    minSeconds: number;
    maxSeconds: number;
    coverImageRequired: boolean;
  };
  caption: {
    maxChars: number;
    sweetSpotChars: [number, number];
    hashtagSweetSpot: [number, number];
    linkPolicy: 'inline' | 'bio-only' | 'first-comment';
  };
}

export const PLATFORM_SPECS: Record<PlatformId, PlatformSpec> = {
  instagram: {
    image: {
      // Feed images accept 4:5 to 1.91:1 (JPEG, 8 MB); 9:16 is a Story shape, not a feed one.
      aspectRatios: ['4:5', '1:1', '1.91:1'],
      recommendedAspectRatio: '4:5',
      maxFileMB: INSTAGRAM_LIMITS.image.maxMB,
    },
    video: {
      aspectRatios: ['9:16', '1:1'],
      recommendedAspectRatio: '9:16',
      // Reels run 3 s to 15 min (ig-user/media, read 2026-11-03). Stories are
      // shorter (INSTAGRAM_LIMITS.story); this entry is the Reel, the wider one,
      // because every standalone IG video publishes as a Reel unless the
      // placement says Story.
      minSeconds: INSTAGRAM_LIMITS.reel.minSeconds,
      maxSeconds: INSTAGRAM_LIMITS.reel.maxSeconds,
      coverImageRequired: true,
    },
    caption: {
      maxChars: 2200,
      sweetSpotChars: [125, 400],
      hashtagSweetSpot: [5, 10],
      linkPolicy: 'bio-only',
    },
  },
  tiktok: {
    image: { aspectRatios: ['9:16'], recommendedAspectRatio: '9:16', maxFileMB: 20 },
    video: {
      aspectRatios: ['9:16'],
      recommendedAspectRatio: '9:16',
      minSeconds: 3,
      maxSeconds: 600,
      // The API takes a cover timestamp (video) or index (photo), never an image.
      coverImageRequired: false,
    },
    caption: {
      maxChars: 2200,
      sweetSpotChars: [80, 150],
      hashtagSweetSpot: [3, 6],
      linkPolicy: 'bio-only',
    },
  },
  linkedin: {
    image: {
      aspectRatios: ['1.91:1', '1:1', '4:5'],
      recommendedAspectRatio: '1.91:1',
      maxFileMB: 100,
    },
    video: {
      aspectRatios: ['16:9', '1:1'],
      recommendedAspectRatio: '16:9',
      minSeconds: 3,
      maxSeconds: 1800,
      coverImageRequired: false,
    },
    caption: {
      maxChars: 3000,
      sweetSpotChars: [800, 1500],
      hashtagSweetSpot: [3, 5],
      linkPolicy: 'inline',
    },
  },
  twitter: {
    image: { aspectRatios: ['16:9', '1:1'], recommendedAspectRatio: '16:9', maxFileMB: 5 },
    video: {
      aspectRatios: ['16:9', '1:1'],
      recommendedAspectRatio: '16:9',
      minSeconds: 1,
      maxSeconds: 140,
      coverImageRequired: false,
    },
    caption: {
      maxChars: 280,
      sweetSpotChars: [70, 120],
      hashtagSweetSpot: [1, 2],
      linkPolicy: 'inline',
    },
  },
  facebook: {
    image: {
      aspectRatios: ['1.91:1', '1:1', '4:5'],
      recommendedAspectRatio: '1.91:1',
      // Page photos: 10 MB (page/photos, read 2026-11-03).
      maxFileMB: FACEBOOK_LIMITS.photo.maxMB,
    },
    video: {
      aspectRatios: ['16:9', '1:1', '9:16'],
      recommendedAspectRatio: '16:9',
      minSeconds: 1,
      // Meta's video-requirements chart says 240 MINUTES max for feed video;
      // the old value 240 transcribed that figure into a seconds field.
      maxSeconds: 14400,
      coverImageRequired: false,
    },
    caption: {
      maxChars: 63206,
      sweetSpotChars: [40, 80],
      hashtagSweetSpot: [1, 3],
      linkPolicy: 'inline',
    },
  },
  youtube: {
    image: { aspectRatios: ['16:9'], recommendedAspectRatio: '16:9', maxFileMB: 2 },
    video: {
      aspectRatios: ['16:9', '9:16'],
      recommendedAspectRatio: '16:9',
      minSeconds: 15,
      maxSeconds: 43200,
      coverImageRequired: true,
    },
    caption: {
      maxChars: 5000,
      sweetSpotChars: [200, 800],
      hashtagSweetSpot: [3, 5],
      linkPolicy: 'inline',
    },
  },
  pinterest: {
    image: { aspectRatios: ['2:3', '1:1'], recommendedAspectRatio: '2:3', maxFileMB: 20 },
    video: {
      aspectRatios: ['9:16', '1:1', '2:3'],
      recommendedAspectRatio: '9:16',
      minSeconds: 4,
      // Pinterest pin specs: 4 seconds to 5 minutes.
      maxSeconds: 300,
      coverImageRequired: true,
    },
    caption: {
      maxChars: 500,
      sweetSpotChars: [100, 250],
      hashtagSweetSpot: [2, 8],
      linkPolicy: 'inline',
    },
  },
  threads: {
    image: { aspectRatios: ['1:1', '4:5'], recommendedAspectRatio: '1:1', maxFileMB: 8 },
    video: {
      aspectRatios: ['9:16', '1:1'],
      recommendedAspectRatio: '9:16',
      minSeconds: 1,
      maxSeconds: 300,
      coverImageRequired: false,
    },
    caption: {
      maxChars: 500,
      sweetSpotChars: [100, 250],
      hashtagSweetSpot: [0, 2],
      linkPolicy: 'inline',
    },
  },
};

/** The product calls the rebranded platform `x`; existing specs and API contracts use `twitter`. */
const PLATFORM_SPEC_ALIASES: Record<string, PlatformId> = {
  x: 'twitter',
};

export function getPlatformSpec(id: string): PlatformSpec | null {
  const key = PLATFORM_SPEC_ALIASES[id] ?? id;

  // Own-property check: bare `in` also matches Object.prototype member names
  // ('toString', 'constructor', …) and returned the inherited built-in.
  return Object.prototype.hasOwnProperty.call(PLATFORM_SPECS, key)
    ? PLATFORM_SPECS[key as PlatformId]
    : null;
}
